/**
 * useHotLeadBadge · the sidebar's Lead Pool badge = unclaimed HOT leads the user can see (L6).
 *
 * One shared poller for the whole app, however many sidebars are mounted (desktop + mobile sheet):
 * reference-counted, started by the first subscriber, stopped by the last, paused while the tab is
 * hidden. The pool page's own 30 s pulse read publishes here too (`publishPulse`), so the badge
 * and the page never show different numbers.
 */
import { useEffect, useSyncExternalStore } from 'react'
import { authFetch } from '@/lib/api'
import { useAuthStore } from '@/stores/auth-store'
import { shouldPoll } from '../poolDispatch'
import type { ApiResponse, PoolPulse } from '../types'

/** The sidebar is lighter than the board: once a minute, plus every board read. */
const BADGE_POLL_MS = 60_000

let badge: number | null = null
/** The last pulse seen anywhere — read by `useAtHoldingCap` without a fetch of its own. */
let lastPulse: PoolPulse | null = null
/** When a pulse last arrived from anywhere (the board's own 30 s read included). */
let lastPublishedAt = 0
const listeners = new Set<() => void>()

function setBadge(next: number | null) {
  if (next === badge) return
  badge = next
  for (const l of listeners) l()
}

/** Feed a fresh pulse into the badge (called by every pulse read). */
export function publishPulse(pulse: Pick<PoolPulse, 'hotUnclaimedForBadge'> | null | undefined): void {
  const n = pulse?.hotUnclaimedForBadge
  lastPublishedAt = Date.now()
  const full = pulse && 'queues' in pulse ? (pulse as PoolPulse) : null
  if (full !== lastPulse) {
    lastPulse = full
    for (const l of listeners) l()
  }
  setBadge(typeof n === 'number' && Number.isFinite(n) ? n : null)
}

/** Forget the number — on logout, so the next user never sees the previous user's count. */
export function clearPulseBadge(): void {
  lastPublishedAt = 0
  lastPulse = null
  setBadge(null)
  for (const l of listeners) l()
}

const getPulseSnapshot = () => lastPulse

/** The most recent pulse (from the board or the sidebar poll); null before any, or after logout. */
export function useLastPulse(): PoolPulse | null {
  return useSyncExternalStore(subscribe, getPulseSnapshot, getPulseSnapshot)
}

/** Whether the sidebar's own poll can skip: a pulse arrived within the interval already. */
export function badgeIsFresh(publishedAt: number, now: number, intervalMs: number): boolean {
  return publishedAt > 0 && now - publishedAt < intervalMs
}

let authWatched = false
function watchLogout() {
  if (authWatched) return
  authWatched = true
  useAuthStore.subscribe((s) => {
    if (!s.tokens?.accessToken) clearPulseBadge()
  })
}

async function fetchPulse(): Promise<void> {
  const token = useAuthStore.getState().tokens?.accessToken
  if (!token) return
  try {
    const resp = await authFetch<ApiResponse<PoolPulse>>('/api/v1/leads/pool/pulse', token, {
      suppressPermissionToast: true,
    })
    if (resp.success) publishPulse(resp.data)
  } catch {
    // Non-critical: keep the last number rather than flashing the badge away.
  }
}

let pollers = 0
let stopPolling: (() => void) | null = null

function startPolling(): () => void {
  watchLogout()
  pollers += 1
  if (pollers === 1) {
    let last: number | null = null
    const tick = () => {
      const now = Date.now()
      if (!shouldPoll(last, now, BADGE_POLL_MS, document.hidden)) return
      last = now
      // The board publishes every 30 s while open; no second request when it just did.
      if (badgeIsFresh(lastPublishedAt, now, BADGE_POLL_MS)) return
      void fetchPulse()
    }
    // First read regardless of visibility (a background tab still gets its badge); then visible-only.
    last = Date.now()
    if (!badgeIsFresh(lastPublishedAt, last, BADGE_POLL_MS)) void fetchPulse()
    const id = window.setInterval(tick, BADGE_POLL_MS)
    const onVisibility = () => {
      if (!document.hidden) tick()
    }
    document.addEventListener('visibilitychange', onVisibility)
    stopPolling = () => {
      window.clearInterval(id)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }
  return () => {
    pollers -= 1
    if (pollers === 0) {
      stopPolling?.()
      stopPolling = null
    }
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

const getSnapshot = () => badge

/**
 * The badge number, or undefined when it should not show (disabled, unknown, or zero).
 * `enabled` = the user holds `sales:access` and the Lead Pool entry is on screen.
 */
export function useHotLeadBadge(enabled: boolean): number | undefined {
  const value = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
  useEffect(() => {
    if (!enabled) return
    return startPolling()
  }, [enabled])
  return enabled && value != null && value > 0 ? value : undefined
}
